import React, { useState, useEffect, useCallback } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 20;

function getAuthHeaders() {
  const sessionToken = sessionStorage.getItem('sessionToken');
  if (!sessionToken) return {};
  return { Authorization: `Bearer ${sessionToken}` };
}

function CreateUser() {
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedParent, setSelectedParent] = useState(null);
  const [parentUsers, setParentUsers] = useState([]);
  const [parentUsersLoading, setParentUsersLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [createdUsers, setCreatedUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editParent, setEditParent] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  const fetchParentUsers = useCallback(() => {
    setParentUsersLoading(true);
    fetch(`${BASE_URL}/admin/users/parents?limit=500&offset=0`, {
      method: 'GET',
      credentials: 'include',
      headers: getAuthHeaders(),
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        const users = data?.data?.users ?? data?.users ?? [];
        setParentUsers(Array.isArray(users) ? users : []);
      })
      .catch(() => setParentUsers([]))
      .finally(() => setParentUsersLoading(false));
  }, []);

  useEffect(() => {
    fetchParentUsers();
  }, [fetchParentUsers]);

  const fetchCreatedUsers = useCallback(async (pageOverride) => {
    setListError('');
    setListLoading(true);
    const currentPage = pageOverride !== undefined ? pageOverride : page;
    const offset = currentPage * PAGE_SIZE;
    try {
      const response = await fetch(
        `${BASE_URL}/admin/users/created-by-admin?limit=${PAGE_SIZE}&offset=${offset}`,
        { method: 'GET', credentials: 'include', headers: getAuthHeaders() }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setListError(data?.message || data?.error || 'Failed to load users');
        setCreatedUsers([]);
        return;
      }
      const users = data?.data?.users ?? data?.users ?? [];
      const total = data?.data?.total ?? data?.total ?? users.length;
      setCreatedUsers(Array.isArray(users) ? users : []);
      setTotalCount(typeof total === 'number' ? total : users.length);
    } catch (err) {
      setListError(err?.message || 'Failed to load users');
      setCreatedUsers([]);
    } finally {
      setListLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchCreatedUsers();
  }, [fetchCreatedUsers]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const usernameTrimmed = username.trim();
    const emailTrimmed = email.trim();
    const phoneTrimmed = phone.trim();

    if (!usernameTrimmed) {
      setError('Username is required');
      return;
    }
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setLoading(true);

    const payload = {
      username: usernameTrimmed,
      password: password.trim(),
    };
    if (emailTrimmed) payload.email = emailTrimmed;
    if (phoneTrimmed) payload.phone = phoneTrimmed;
    if (name.trim()) payload.name = name.trim();
    if (selectedParent) {
      payload.parent_user_id = selectedParent.id ?? selectedParent.user_id;
    }

    try {
      const response = await fetch(`${BASE_URL}/admin/users`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data?.message || data?.error || 'Failed to create user');
        return;
      }
      const userType = selectedParent ? 'Child user' : 'Parent user';
      setSuccess(`${userType} created successfully.`);
      setUsername('');
      setName('');
      setEmail('');
      setPassword('');
      setPhone('');
      setSelectedParent(null);
      setPage(0);
      fetchCreatedUsers(0);
      if (!selectedParent) fetchParentUsers();
    } catch (err) {
      setError(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Edit handlers
  const handleEditOpen = (user) => {
    setEditUser(user);
    setEditUsername(user.username ?? user.user_name ?? '');
    setEditName(user.name ?? '');
    setEditEmail(user.email ?? '');
    setEditPhone(user.phone ?? '');
    setEditPassword('');
    setEditError('');
    // Find the parent user object in parentUsers list
    const parentId = user.parent_user_id;
    if (parentId) {
      const found = parentUsers.find((p) => p.id === parentId);
      setEditParent(found || null);
    } else {
      setEditParent(null);
    }
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditUser(null);
  };

  const handleEditSubmit = async () => {
    setEditError('');
    if (!editUsername.trim()) {
      setEditError('Username cannot be empty');
      return;
    }

    setEditLoading(true);

    const payload = {};
    const orig = editUser;

    if (editUsername.trim() !== (orig.username ?? orig.user_name ?? '')) {
      payload.username = editUsername.trim();
    }
    if (editName.trim() !== (orig.name ?? '')) {
      payload.name = editName.trim();
    }
    if ((editEmail.trim() || '') !== (orig.email ?? '')) {
      payload.email = editEmail.trim();
    }
    if ((editPhone.trim() || '') !== (orig.phone ?? '')) {
      payload.phone = editPhone.trim();
    }
    if (editPassword.trim()) {
      payload.password = editPassword.trim();
    }

    const newParentId = editParent?.id ?? null;
    const oldParentId = orig.parent_user_id ?? null;
    if (newParentId !== oldParentId) {
      payload.parent_user_id = newParentId;
    }

    if (Object.keys(payload).length === 0) {
      setEditError('No changes to save');
      setEditLoading(false);
      return;
    }

    try {
      const userId = orig.id ?? orig.user_id;
      const response = await fetch(`${BASE_URL}/admin/users/${userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setEditError(data?.message || data?.error || 'Failed to update user');
        return;
      }
      setEditOpen(false);
      setEditUser(null);
      setSuccess('User updated successfully.');
      fetchCreatedUsers(page);
      fetchParentUsers();
    } catch (err) {
      setEditError(err?.message || 'Something went wrong');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center ">
                  <IconButton
                    style={{ background: '#FFFFFF' }}
                    component={Link}
                    to="/user-dashboard"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <h3 style={{ marginLeft: '20px' }}>Create User</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Paper elevation={0} sx={{ p: 2, maxWidth: 560 }}>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                      </Alert>
                    )}
                    {success && (
                      <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                      </Alert>
                    )}
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Autocomplete
                            fullWidth
                            options={parentUsers}
                            loading={parentUsersLoading}
                            getOptionLabel={(option) =>
                              `${option.id ?? '—'} - ${option.username ?? ''}${option.name ? ` (${option.name})` : ''}`
                            }
                            value={selectedParent}
                            onChange={(_, newValue) => setSelectedParent(newValue)}
                            isOptionEqualToValue={(option, value) =>
                              (option.id ?? option.user_id) === (value?.id ?? value?.user_id)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Parent User (optional)"
                                variant="outlined"
                                helperText="Leave empty to create a Parent User. Select a parent to create a Child User."
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            variant="outlined"
                            helperText="Required. Must be unique."
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            type="password"
                            label="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            variant="outlined"
                            helperText={selectedParent ? "Required." : "Required. This will be the user's login password."}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Name (optional)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email (optional)"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            variant="outlined"
                            helperText="Optional. Must be unique if provided."
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Phone (optional)"
                            placeholder="+919876543210"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            variant="outlined"
                            helperText="Optional. Must be unique if provided."
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                          >
                            {loading ? 'Creating...' : selectedParent ? 'Create Child User' : 'Create Parent User'}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </Paper>
                </CardContent>
              </Card>

              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Users created by admin
                  </Typography>
                  {listError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListError('')}>
                      {listError}
                    </Alert>
                  )}
                  {listLoading ? (
                    <Grid container justifyContent="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </Grid>
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>ID</TableCell>
                              <TableCell>Username</TableCell>
                              <TableCell>Name</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>Phone</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Parent</TableCell>
                              <TableCell>Created</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {createdUsers.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={9} align="center">
                                  No users created yet
                                </TableCell>
                              </TableRow>
                            ) : (
                              createdUsers.map((row, index) => (
                                <TableRow key={row.id ?? row.user_id ?? `row-${index}`}>
                                  <TableCell>{row.id ?? row.user_id ?? '—'}</TableCell>
                                  <TableCell>{row.username ?? row.user_name ?? '—'}</TableCell>
                                  <TableCell>{row.name ?? '—'}</TableCell>
                                  <TableCell>{row.email ?? '—'}</TableCell>
                                  <TableCell>{row.phone ?? '—'}</TableCell>
                                  <TableCell>{row.parent_user_id ? 'Child' : 'Parent'}</TableCell>
                                  <TableCell>{row.parent_user_id ?? '—'}</TableCell>
                                  <TableCell>
                                    {row.created_at ?? row.createdAt
                                      ? new Date(row.created_at ?? row.createdAt).toLocaleString()
                                      : '—'}
                                  </TableCell>
                                  <TableCell>
                                    <IconButton size="small" onClick={() => handleEditOpen(row)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {totalCount > PAGE_SIZE && (
                        <TablePagination
                          component="div"
                          count={totalCount}
                          page={page}
                          onPageChange={handlePageChange}
                          rowsPerPage={PAGE_SIZE}
                          rowsPerPageOptions={[PAGE_SIZE]}
                        />
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setEditError('')}>
              {editError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={parentUsers.filter((p) => p.id !== (editUser?.id ?? editUser?.user_id))}
                loading={parentUsersLoading}
                getOptionLabel={(option) =>
                  `${option.id ?? '—'} - ${option.username ?? ''}${option.name ? ` (${option.name})` : ''}`
                }
                value={editParent}
                onChange={(_, newValue) => setEditParent(newValue)}
                isOptionEqualToValue={(option, value) =>
                  (option.id ?? option.user_id) === (value?.id ?? value?.user_id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent User"
                    variant="outlined"
                    helperText="Leave empty for Parent User."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                value={editUsername}
                onChange={(e) => setEditUsername(e.target.value)}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="New Password"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                variant="outlined"
                helperText="Leave blank to keep current password."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={editLoading}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Component>
  );
}

export default CreateUser;
